import React from 'react'
import { Button } from '@reapit/elements/core/button'
import { AdminFormField } from '../../../../api/types'
import { FieldPreview } from '../field-preview'
import { FieldConditionBadge, FieldPreviewGrid, SectionEditRow } from '../../style'
import { CollectionBox, CollectionFooter, CollectionMeta, CollectionTitle } from './style'

interface CollectionPreviewProps {
  collection: AdminFormField
  /** The collection's child (item template) fields, ordered. */
  childFields: AdminFormField[]
  /** Visibility gate(s) - shown when ANY match (empty = always shown). */
  conditions?: { controllingLabel: string; optionLabel: string }[]
  onEditChild: (child: AdminFormField) => void
  onRemoveChild: (child: AdminFormField) => void
  onEditCollection: () => void
  onRemove: () => void
  /** Hides all Edit/Delete/Add-item affordances — set when the environment is read-only. */
  readOnly?: boolean
}

/** Renders a Collection as a dashed repeatable "item template" holding its child field previews. */
export const CollectionPreview: React.FC<CollectionPreviewProps> = ({
  collection,
  childFields,
  conditions = [],
  onEditChild,
  onRemoveChild,
  onEditCollection,
  onRemove,
  readOnly = false,
}) => {
  const title = collection.editDisplayName || collection.jsonObjectName || 'Collection'
  const bounds =
    collection.min != null || collection.max != null ? ` · ${collection.min ?? 0}–${collection.max ?? '∞'} items` : ''
  const addLabel = collection.editAddSectionButtonText || 'Add item'

  return (
    <CollectionBox>
      {conditions.length > 0 && (
        <FieldConditionBadge
          title={`Only shows when ${conditions
            .map((condition) => `${condition.controllingLabel} = ${condition.optionLabel}`)
            .join(' OR ')}`}
        >
          Conditional visibility
        </FieldConditionBadge>
      )}
      <div>
        <CollectionTitle>{title}</CollectionTitle>
        <CollectionMeta>
          Repeatable · “{addLabel}”{bounds}
        </CollectionMeta>
      </div>

      {childFields.length > 0 && (
        <FieldPreviewGrid>
          {childFields.map((child) => (
            <FieldPreview
              key={child.referenceID}
              field={child}
              onEdit={() => onEditChild(child)}
              onRemove={() => onRemoveChild(child)}
              readOnly={readOnly}
            />
          ))}
        </FieldPreviewGrid>
      )}

      {!readOnly && (
        <CollectionFooter>
          <SectionEditRow>
            <Button variant="tertiary" useLinkStyle size="small" type="button" onClick={onEditCollection}>
              Edit
            </Button>
            <Button variant="tertiary" useLinkStyle isDestructive size="small" type="button" onClick={onRemove}>
              Delete
            </Button>
          </SectionEditRow>
        </CollectionFooter>
      )}
    </CollectionBox>
  )
}
